import React from 'react';
import { Row, Col, Space, Input, Select, Button, Switch } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ProjectFilters } from '../../../types/project';
import { useAuth } from '../../../hooks/useAuth';
import { usePermissions } from '../../../contexts/PermissionContext';

const { Option } = Select;

interface ProjectFilterProps {
  filters: ProjectFilters;
  onFiltersChange: (newFilters: Partial<ProjectFilters>) => void;
  onReset: () => void;
  loading?: boolean;
  projectGroups: Array<{ id: number; name: string }>;
  projectGroupsLoading: boolean;
  customers: Array<{ id: string; username: string }>;
  customersLoading: boolean;
}

const ProjectFilter: React.FC<ProjectFilterProps> = ({
  filters,
  onFiltersChange,
  onReset,
  loading,
  projectGroups,
  projectGroupsLoading,
  customers,
  customersLoading
}) => {
  const { user } = useAuth();
  const { hiddenFields } = usePermissions();
  const hidden = hiddenFields('project.Project');
  const strategyHideCustomer = Array.isArray(hidden) && hidden.includes('customer__username');
  const isCustomerRole = user?.role === 'customer';
  const hideCustomer = strategyHideCustomer || isCustomerRole;
  console.info('[ProjectFilter] hiddenFields(project.Project)=', hidden, 'hideCustomer=', hideCustomer);

  // 处理搜索输入变化
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onFiltersChange({ search: value });
  };

  // 处理项目类型变化
  const handleProjectTypeChange = (value: string) => {
    onFiltersChange({ project_type: value });
  };

  // 处理项目状态变化
  const handleProjectStatusChange = (value: string) => {
    onFiltersChange({ project_status: value });
  };

  // 处理项目组变化
  const handleProjectGroupChange = (value: string) => {
    onFiltersChange({ project_group: value });
  };

  // 处理客户变化
  const handleCustomerChange = (value: string) => {
    onFiltersChange({ customer: value });
  };

  // 处理“仅显示未完成基础配置项目”开关
  const handleMissingInventoryConfigChange = (checked: boolean) => {
    onFiltersChange({ missing_inventory_config: checked });
  };

  // 处理搜索
  const handleSearch = () => {
    // 搜索时不需要额外处理，筛选条件变化会自动触发
  };

  // 处理重置
  const handleReset = () => {
    onReset();
  };

    return (
    <div style={{ marginBottom: 16 }}>
      <Row gutter={[6, 6]} align="middle">
        <Col flex="none">
          <Space size={4}>
            <span style={{ fontWeight: 500, color: '#333', fontSize: '14px' }}>搜索：</span>
            <Input
              placeholder="项目名称/编码"
              value={filters.search}
              onChange={handleSearchInputChange}
              allowClear
              style={{ width: 220 }}
              size="small"
            />
          </Space>
        </Col>
        <Col flex="none">
          <Space size={4}>
            <span style={{ fontWeight: 500, color: '#333', fontSize: '14px' }}>项目类型：</span>
            <Select
              placeholder="请选择项目类型"
              value={filters.project_type || undefined}
              onChange={handleProjectTypeChange}
              allowClear
              style={{ width: 180 }}
              size="small"
            >
              <Option value="OrderFulfillment">一件代发</Option>
              <Option value="StockTransfer">备货中转</Option>
              <Option value="Relabeling">贴换标/清点</Option>
              <Option value="ReturnProcessing">退货/退仓处理</Option>
              <Option value="Mixed">多种业务模式混合</Option>
              <Option value="Other">其他</Option>
            </Select>
          </Space>
        </Col>
        <Col flex="none">
          <Space size={4}>
            <span style={{ fontWeight: 500, color: '#333', fontSize: '14px' }}>项目状态：</span>
            <Select
              placeholder="请选择项目状态"
              value={filters.project_status || undefined}
              onChange={handleProjectStatusChange}
              allowClear
              style={{ width: 120 }}
              size="small"
            >
              <Option value="created">已创建</Option>
              <Option value="terminated">终止</Option>
              <Option value="exception">异常</Option>
            </Select>
          </Space>
        </Col>
        <Col flex="none">
          <Space size={4}>
            <span style={{ fontWeight: 500, color: '#333', fontSize: '14px' }}>项目组：</span>
            <Select
              placeholder="请选择项目组"
              value={filters.project_group || undefined}
              onChange={handleProjectGroupChange}
              allowClear
              loading={projectGroupsLoading}
              style={{ width: 120 }}
              size="small"
            >
              {projectGroups.map(group => (
                <Option key={group.id} value={group.id.toString()}>
                  {group.name}
                </Option>
              ))}
            </Select>
          </Space>
        </Col>
        {!hideCustomer && (
          <Col flex="none">
            <Space size={4}>
              <span style={{ fontWeight: 500, color: '#333', fontSize: '14px' }}>客户：</span>
              <Select
                placeholder="请选择客户"
                value={filters.customer || undefined}
                onChange={handleCustomerChange}
                allowClear
                loading={customersLoading}
                style={{ width: 120 }}
                listHeight={320}
                dropdownMatchSelectWidth={260}
                size="small"
              >
                {customers.map(customer => (
                  <Option key={customer.id} value={customer.id.toString()}>
                    {customer.username}
                  </Option>
                ))}
              </Select>
            </Space>
          </Col>
        )}
        <Col flex="none">
          <Space size={4}>
            <span style={{ fontWeight: 500, color: '#333', fontSize: '14px' }}>仅显示未完成基础配置项目：</span>
            <Switch
              size="small"
              checked={
                filters.missing_inventory_config === true ||
                String((filters as any).missing_inventory_config).toLowerCase() === 'true'
              }
              onChange={handleMissingInventoryConfigChange}
            />
          </Space>
        </Col>
        <Col flex="none">
          <Space size={4}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
              size="small"
            >
              搜索
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}
              size="small"
            >
              重置
            </Button>
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default ProjectFilter;
